/**
 * routerMigration.js — PERMANENT URL migration shim for XOL-209.
 *
 * This shim is intentionally PERMANENT. It rewrites legacy URL patterns to
 * their canonical d.<component>.<args> equivalents so that bookmarked links,
 * shared links, and external references continue to work after the route
 * records change. Do NOT remove or time-box this shim — old URLs may live
 * in bookmarks, notification emails, and QR codes indefinitely.
 *
 * Wired in src/router/index.js via router.beforeEach.
 *
 * Rules are evaluated in order; first match wins. A null return means no
 * rewrite is needed and navigation proceeds normally.
 *
 * Rule return shapes:
 *   string        — new path only (original behaviour, PR 1/PR 2 rules)
 *   {path, query} — new path + query params (PR 3 request rules, sharerId)
 * router.beforeEach handles both shapes — see src/router/index.js.
 */

import { INTERNAL_TO_SLUG } from '@/lib/requestTypeSlugs'

var RULES = [
  // /sites/site/:siteId(/:tab(/:subtab(/:subsubtab))) — legacy nested route
  [
    /^\/sites\/site\/(\d+)(?:\/([^/]+))?(?:\/([^/]+))?(?:\/([^/]+))?\/?$/,
    function (m) {
      return '/sites/d.site.' + m[1] +
        (m[2] ? '.' + m[2] : '') +
        (m[3] ? '.' + m[3] : '') +
        (m[4] ? '.' + m[4] : '')
    }
  ],

  // PR-1 canary: /utility/:utilityId/bill/:billId → nested siteUtil L1 + bill L2
  // Legacy mounted SiteUtilTable as L1 (bills list for that site) and bill
  // detail as L2. New grammar preserves both layers so closing the bill
  // returns to the site's bill list, not the /utility list.
  [
    /^\/utility\/(\d+)\/bill\/(\d+)\/?$/,
    function (m) {
      return '/utility/d.siteUtil.' + m[1] + '/d.bill.' + m[2]
    }
  ],

  // PR-1 canary: /utility/:utilityId — site's bill list (was SiteUtilTable as L1)
  [
    /^\/utility\/(\d+)\/?$/,
    function (m) {
      return '/utility/d.siteUtil.' + m[1]
    }
  ],

  // Legacy /sites/:view selector (active|candidate|inactive|dismantled|all)
  [
    /^\/sites\/(active|candidate|inactive|dismantled|all)\/?$/,
    function (m) {
      return '/sites?view=' + m[1]
    }
  ],

  // Legacy /sites/:view/site/:siteId(/:tab(/:subtab(/:subsubtab)))
  // View+drawer combination is rare. Degrade: drop the view filter and
  // redirect to the canonical drawer URL. The simpler path avoids the
  // #__d hash workaround which proved brittle in practice.
  [
    /^\/sites\/(active|candidate|inactive|dismantled|all)\/site\/(\d+)(?:\/([^/]+))?(?:\/([^/]+))?(?:\/([^/]+))?\/?$/,
    function (m) {
      return '/sites/d.site.' + m[2] +
        (m[3] ? '.' + m[3] : '') +
        (m[4] ? '.' + m[4] : '') +
        (m[5] ? '.' + m[5] : '')
    }
  ],

  // XOL-209 PR 3: /requests legacy URL rewrites
  // /requests/<internal>/<sharerId>/request/<id> → /requests/<slug>/d.request.<id>?sharerId=<sharerId>
  [
    /^\/requests\/(report|acquire|share|upgrade|backhaul|dismantle|calendar)\/(\d+)\/request\/(\d+)\/?$/,
    function (m) {
      return { path: '/requests/' + INTERNAL_TO_SLUG[m[1]] + '/d.request.' + m[3], query: { sharerId: m[2] } }
    }
  ],

  // /requests/<internal>/<sharerId> → /requests/<slug>?sharerId=<sharerId>
  [
    /^\/requests\/(report|acquire|share|upgrade|backhaul|dismantle|calendar)\/(\d+)\/?$/,
    function (m) {
      return { path: '/requests/' + INTERNAL_TO_SLUG[m[1]], query: { sharerId: m[2] } }
    }
  ],

  // /requests/<internal> → /requests/<slug>  (covers /requests/report → /requests/summary)
  [
    /^\/requests\/(report|acquire|share|upgrade|backhaul|dismantle|calendar)\/?$/,
    function (m) { return '/requests/' + INTERNAL_TO_SLUG[m[1]] }
  ]
]

/**
 * Attempt to rewrite `path` to its canonical equivalent.
 * Returns the new path string, or null if no rule matched.
 *
 * @param {string} path  — e.g. "/sites/site/42/finance/sharers"
 * @returns {string|null}
 */
export function migrationRewrite (path) {
  for (var i = 0; i < RULES.length; i++) {
    var re = RULES[i][0]
    var fn = RULES[i][1]
    var m = path.match(re)
    if (m) return fn(m)
  }
  return null
}
